import { Grow, styled } from '@mui/material'
import {
  Children,
  ReactNode,
  cloneElement,
  memo,
  useEffect,
  useState,
} from 'react'
import { ReactComponent as IconArrow } from './assets/select-arrow.svg'
import clsx from 'clsx'
import { ExSelectItem } from './ExSelectItem'

export type ExSelectValue = string | number | undefined

export interface ExSelectProps {
  label: string
  value?: ExSelectValue
  onChange?: (val: any) => void
  children?: ReactNode
}

const ExSelectWrap = styled('div')<{ isin: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  height: 40px;
  font-size: 14px;
  line-height: 20px;
  padding: 10px 12px;
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  position: relative;
  user-select: none;
  background-color: #1e2329;

  border-bottom: ${({ isin }) =>
    isin === 1 ? '1px solid rgba(160, 238, 9, 1)' : '1px solid #2e363b'};

  transition: border-color 0.1s ease-in-out;

  .arrow {
    transition: transform 0.2s ease-in-out;
  }
  &:hover {
    /* border-color: #52585e; */
    cursor: pointer;
  }

  &.ExSelectWrap-active {
    /* border-color: rgba(160, 238, 9, 1); */
    z-index: 1010;

    .arrow {
      transform: rotate(180deg);
    }
  }
`
const ExSelectLabel = styled('span')`
  color: rgba(149, 164, 173, 1);
`
const ExSelectValue = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  gap: 4px;
`
const ExSelectArrow = styled(IconArrow)`
  color: rgba(149, 164, 173, 1);
`
const ExSelectPopupBox = styled('div')`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 100%;
`
const ExSelectPopup = styled('div')<{ isin: number }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  gap: 4px;
  /* padding: 8px; */
  /* background-color: red; */
  background-color: rgba(30, 35, 41, 1);
  /* border: 1px solid rgba(42, 49, 57, 1); */
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  /* border-top: ${({ isin }) =>
    isin === 1 ? '1px solid rgba(160, 238, 9, 1)' : '1px solid transparent'}; */
`

export const ExSelect = memo<ExSelectProps>(
  ({ label, value, onChange, children }) => {
    const [selectedValue, setSelectedValue] = useState<ExSelectValue>(value)
    const [selectedLabel, setSelectedLabel] = useState<string>('')
    const [active, setActive] = useState(false)
    const [styles, setStyles] = useState<{
      left?: string
      top?: string
      width?: string
    }>({})

    const handleItemClick = (v: any) => {
      setActive(false)
      setSelectedValue(v)
    }

    useEffect(() => {
      setSelectedValue(value)
    }, [value])

    useEffect(() => {
      const label = (Children.toArray(children) as any).find(
        (child: any) => child.props?.value === selectedValue,
      )?.props.children as string

      setSelectedLabel(label)
    }, [selectedValue])

    useEffect(() => {
      onChange && onChange(selectedValue)
    }, [selectedValue])

    const modifiedChildren = Children.map(children as any, (child: any) => {
      if (child.type === ExSelectItem) {
        return cloneElement(child, {
          key: child.props.value,
          selected: child.props.value === selectedValue,
          onClick: () => handleItemClick(child.props.value),
        })
      }
      return child
    })

    return (
      <>
        <ExSelectWrap
          isin={active ? 1 : 0}
          className={clsx({ 'ExSelectWrap-active': active })}
          onClick={(event) => {
            const t = event.currentTarget
            const bounding = t.getBoundingClientRect()
            setActive(!active)
            setStyles({
              left: `${bounding.left}px`,
              top: `${bounding.top + bounding.height}px`,
              width: `${bounding.width}px`,
            })
          }}
        >
          <ExSelectLabel>{label}</ExSelectLabel>{' '}
          <ExSelectValue>
            {selectedLabel}
            <ExSelectArrow className={'arrow'} />
          </ExSelectValue>
        </ExSelectWrap>

        {active && (
          <ExSelectPopupBox
            onScrollCapture={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onScroll={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setActive(false)
              }
            }}
          >
            <Grow in={active} timeout={200}>
              <ExSelectPopup style={styles} isin={active ? 1 : 0}>
                {modifiedChildren}
              </ExSelectPopup>
            </Grow>
          </ExSelectPopupBox>
        )}
      </>
    )
  },
)
